import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import { fieldActivityIssuesData } from "types/api-types/field/field-activity-issues/field-activity-issues.types";

interface IssueFormData {
  jobId: number | null;
  panelId: number | null;
  building: string;
  location: string;
  gridLine: string;
  issueType: string;
  title: string;
  description: string;
  severity: string;
  category: string;
  affectedComponent: string;
  defectType: string;
  safetyIssue: boolean;
  qualityIssue: boolean;
  weatherRelated: boolean;
  impactLevel: string;
  productionImpact: boolean;
  scheduleImpact: boolean;
  estimatedDelayHours: number;
  estimatedCost: number;
  assignedTo: number | null;
  assignedTeamId: number | null;
  targetResolutionDate: Date | null;
  rootCause: string;
  preventiveAction: string;
  correctiveAction: string;
  photos: File[];
  attachments: File[];
  qcRequired: boolean;
  weatherConditions: string;
  temperature: number | null;
  windSpeed: number | null;
  priority: number;
}

class FieldIssueWorkflowStore {
  rootStore: RootStore;

  // Workflow state
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Issue data
  issueData: IssueFormData = {
    jobId: null,
    panelId: null,
    building: "",
    location: "",
    gridLine: "",
    issueType: "defect",
    title: "",
    description: "",
    severity: "low",
    category: "general",
    affectedComponent: "",
    defectType: "",
    safetyIssue: false,
    qualityIssue: false,
    weatherRelated: false,
    impactLevel: "minor",
    productionImpact: false,
    scheduleImpact: false,
    estimatedDelayHours: 0,
    estimatedCost: 0,
    assignedTo: null,
    assignedTeamId: null,
    targetResolutionDate: null,
    rootCause: "",
    preventiveAction: "",
    correctiveAction: "",
    photos: [],
    attachments: [],
    qcRequired: false,
    weatherConditions: "",
    temperature: null,
    windSpeed: null,
    priority: 3,
  };

  // Existing issues for reference
  existingIssues: any[] = [];
  relatedPanels: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Navigation actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  // Form data actions
  updateIssueData = <K extends keyof IssueFormData>(
    field: K,
    value: IssueFormData[K]
  ) => {
    this.issueData[field] = value;
  };

  selectJob = (jobId: number) => {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    const job = jobStore?.entities.find((j: any) => j.id === jobId);

    runInAction(() => {
      this.issueData.jobId = jobId;
      // Auto-populate location if available from job
      if (job?.address) {
        this.issueData.location = job.address as string;
      }
    });

    // Load related panels for this job
    this.loadRelatedPanels(jobId);
  };

  selectPanel = (panelId: number) => {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const panel = panelStore?.entities.find((p: any) => p.id === panelId);

    runInAction(() => {
      this.issueData.panelId = panelId;
      // Auto-populate building and grid line if available
      if (panel) {
        this.issueData.building = (panel.building as string) || "";
        this.issueData.gridLine = (panel.gridLine as string) || "";
      }
    });
  };

  addPhotos = (files: FileList) => {
    const newPhotos = Array.from(files).filter(
      (file) => file.type.startsWith("image/")
    );
    runInAction(() => {
      this.issueData.photos = [...this.issueData.photos, ...newPhotos];
    });
  };

  removePhoto = (index: number) => {
    this.issueData.photos = this.issueData.photos.filter((_, i) => i !== index);
  };

  addAttachments = (files: FileList) => {
    const newFiles = Array.from(files);
    runInAction(() => {
      this.issueData.attachments = [
        ...this.issueData.attachments,
        ...newFiles,
      ];
    });
  };

  removeAttachment = (index: number) => {
    this.issueData.attachments = this.issueData.attachments.filter(
      (_, i) => i !== index
    );
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Basic information
        if (!this.issueData.jobId) {
          this.setError("Please select a job");
          return false;
        }
        if (!this.issueData.title) {
          this.setError("Please enter an issue title");
          return false;
        }
        if (!this.issueData.description) {
          this.setError("Please enter an issue description");
          return false;
        }
        break;

      case 1: // Location & Component
        if (!this.issueData.location) {
          this.setError("Please specify the location");
          return false;
        }
        break;

      case 2: // Impact Assessment
        if (this.issueData.scheduleImpact && !this.issueData.estimatedDelayHours) {
          this.setError("Please estimate delay hours for schedule impact");
          return false;
        }
        break;

      case 3: // Assignment & Resolution
        if (!this.issueData.assignedTo && !this.issueData.assignedTeamId) {
          this.setError("Please assign the issue to a person or team");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadRelatedPanels = async (jobId: number) => {
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      await panelStore?.find({ query: { jobId, $limit: 100 } });

      runInAction(() => {
        this.relatedPanels = panelStore?.entities.filter(
          (p: any) => p.jobId === jobId
        ) || [];
      });
    } catch (error) {
      console.error("Failed to load panels:", error);
    }
  };

  loadExistingIssues = async () => {
    if (!this.issueData.jobId) return;

    try {
      const fieldActivityIssuesStore = this.rootStore.fieldActivityIssuesStore as ServiceStore;
      await fieldActivityIssuesStore?.find({
        query: {
          jobId: this.issueData.jobId,
          $limit: 50,
          $sort: { createdAt: -1 },
        },
      });

      runInAction(() => {
        this.existingIssues = fieldActivityIssuesStore?.entities.filter(
          (issue: any) => issue.jobId === this.issueData.jobId
        ) || [];
      });
    } catch (error) {
      console.error("Failed to load existing issues:", error);
    }
  };

  submitIssue = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const fieldActivityIssuesStore = this.rootStore.fieldActivityIssuesStore as ServiceStore;
      const currentUser = (this.rootStore.authStore as any)?.user;

      // Prepare issue data for submission
      const issuePayload: fieldActivityIssuesData = {
        jobId: this.issueData.jobId!,
        panelId: this.issueData.panelId || undefined,
        building: this.issueData.building || undefined,
        location: this.issueData.location || undefined,
        gridLine: this.issueData.gridLine || undefined,
        issueType: this.issueData.issueType,
        title: this.issueData.title,
        description: this.issueData.description,
        severity: this.issueData.severity as "Critical" | "Minor" | "Major",
        status: "reported",
        category: this.issueData.category,
        affectedComponent: this.issueData.affectedComponent || undefined,
        defectType: this.issueData.defectType || undefined,
        safetyIssue: this.issueData.safetyIssue,
        qualityIssue: this.issueData.qualityIssue,
        weatherRelated: this.issueData.weatherRelated,
        impactLevel: this.issueData.impactLevel,
        productionImpact: this.issueData.productionImpact,
        scheduleImpact: this.issueData.scheduleImpact,
        estimatedDelayHours: this.issueData.estimatedDelayHours || undefined,
        estimatedCost: this.issueData.estimatedCost || undefined,
        reportedBy: currentUser?.id || 0,
        assignedTo: this.issueData.assignedTo || undefined,
        assignedTeamId: this.issueData.assignedTeamId || undefined,
        targetResolutionDate: this.issueData.targetResolutionDate?.toISOString() || undefined,
        rootCause: this.issueData.rootCause || undefined,
        qcRequired: this.issueData.qcRequired,
        weatherConditions: this.issueData.weatherConditions || undefined,
        temperature: this.issueData.temperature || undefined,
        windSpeed: this.issueData.windSpeed || undefined,
        priority: this.issueData.priority,
        isVisible: true,
      };

      // Handle photo uploads
      if (this.issueData.photos.length > 0) {
        // TODO: Upload photos to storage service and get URLs
        // For now, we'll skip photo upload implementation
        // issuePayload.photos = uploadedPhotoUrls;
      }

      // Handle attachment uploads
      if (this.issueData.attachments.length > 0) {
        // TODO: Upload attachments to storage service
        // For now, we'll skip attachment upload implementation
        // issuePayload.attachments = uploadedAttachments;
      }

      // Create the issue
      const createdIssue = await fieldActivityIssuesStore?.create(issuePayload);

      runInAction(() => {
        this.loading = false;
        this.success = `Field issue "${this.issueData.title}" has been created successfully.`;

        // Navigate to success step
        this.activeStep = 5;
      });

      // Send notifications if critical
      if (this.issueData.severity === "critical" || this.issueData.safetyIssue) {
        // TODO: Trigger notification service
      }

      return createdIssue;
    } catch (error: any) {
      runInAction(() => {
        this.loading = false;
        this.error = error.message || "Failed to create field issue";
      });
      throw error;
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.issueData = {
      jobId: null,
      panelId: null,
      building: "",
      location: "",
      gridLine: "",
      issueType: "defect",
      title: "",
      description: "",
      severity: "low",
      category: "general",
      affectedComponent: "",
      defectType: "",
      safetyIssue: false,
      qualityIssue: false,
      weatherRelated: false,
      impactLevel: "minor",
      productionImpact: false,
      scheduleImpact: false,
      estimatedDelayHours: 0,
      estimatedCost: 0,
      assignedTo: null,
      assignedTeamId: null,
      targetResolutionDate: null,
      rootCause: "",
      preventiveAction: "",
      correctiveAction: "",
      photos: [],
      attachments: [],
      qcRequired: false,
      weatherConditions: "",
      temperature: null,
      windSpeed: null,
      priority: 3,
    };
    this.existingIssues = [];
    this.relatedPanels = [];
  };
}

export default FieldIssueWorkflowStore;